import { useEffect, useState } from "react";
import { AlertTriangle, CircleCheck, Clock } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { BankConnectionStateBadge, bankConnectionFailureLabel } from "@/components/BankConnectionsPage";
import { LoadingState } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import type { components } from "@dolfin/api-admin";

type BankConnection = components["schemas"]["BankConnectionResponse"];

// States the return page stops polling on: the outcome is known, or it now waits on a person.
const SETTLED = new Set(["Connected", "Failed", "Expired", "Revoked", "Disconnected", "AwaitingFurtherAuthorisation"]);
const POLL_INTERVAL_MS = 1500;
const POLL_LIMIT = 40;

/** The callback has already queued the account read, so poll until the connection settles. */
export function BankConnectionReturn({
  bankConnectionId,
  status,
  onDone,
}: {
  bankConnectionId: string | null;
  status: string | null;
  onDone: () => void;
}) {
  const api = useDolfinClient();
  const [connection, setConnection] = useState<BankConnection | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!bankConnectionId) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;

    async function poll() {
      const { data, response } = await api.GET("/v1/bank-connections/{id}", { params: { path: { id: bankConnectionId! } } });
      if (cancelled) return;
      if (response.status === 404) {
        setNotFound(true);
        return;
      }
      if (data) setConnection(data);
      attempts += 1;
      if (data && SETTLED.has(data.state)) return;
      if (attempts >= POLL_LIMIT) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, bankConnectionId]);

  const settled = connection !== null && SETTLED.has(connection.state);

  let body: React.ReactNode;
  if (!bankConnectionId || notFound) {
    body = <p className="text-center text-sm text-muted-foreground">No bank connection to show.</p>;
  } else if (!settled && !timedOut) {
    body = (
      <LoadingState
        label={status === "failed" ? "Checking what the bank said..." : "Back from your bank. Reading your accounts..."}
      />
    );
  } else if (connection?.state === "Connected") {
    body = (
      <Outcome icon={<CircleCheck className="size-6 text-emerald-600" />} title={`${connection.institutionName} is connected`}>
        {connection.accounts.length} account{connection.accounts.length === 1 ? "" : "s"} now show their balances.
      </Outcome>
    );
  } else if (connection?.state === "AwaitingFurtherAuthorisation") {
    body = (
      <Outcome icon={<Clock className="size-6 text-amber-600" />} title="Waiting for a second approver">
        Another holder of the account must approve access in their own banking app. The connection completes once
        they do.
      </Outcome>
    );
  } else if (timedOut) {
    body = (
      <Outcome icon={<Clock className="size-6 text-muted-foreground" />} title="Still waiting for your bank">
        This is taking longer than usual. The connection will update on the bank connections page when the bank
        answers.
      </Outcome>
    );
  } else {
    body = (
      <Outcome icon={<AlertTriangle className="size-6 text-destructive" />} title="The bank was not connected">
        {bankConnectionFailureLabel(connection?.failureReason) ?? "The connection could not be completed."}
        {connection?.providerMessage ? (
          <span className="mt-1 block text-xs">Bank said: {connection.providerMessage}</span>
        ) : null}
      </Outcome>
    );
  }

  return (
    <div className="space-y-4">
      {connection ? (
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="font-medium">{connection.institutionName}</span>
          <BankConnectionStateBadge state={connection.state} />
        </div>
      ) : null}
      {body}
      {settled || timedOut || notFound || !bankConnectionId ? (
        <div className="flex justify-center">
          <Button onClick={onDone}>Back to bank connections</Button>
        </div>
      ) : null}
    </div>
  );
}

function Outcome({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-xl border bg-card/30 px-6 py-12 text-center">
      {icon}
      <p className="mt-3 text-sm font-semibold">{title}</p>
      <p className="mt-1 max-w-md text-sm text-pretty text-muted-foreground">{children}</p>
    </div>
  );
}
